import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';
import { viewerLoadLog, viewerLoadSrcKind } from '../shared/components/document-viewer/document-viewer-load.debug';

/**
 * Centralized, cache-aware state for PDF viewer URLs and preview generation.
 *
 * **Hosts that load raw files or `document_content` should use `getViewerDataUrl()` for display.**
 * The backend `createPreviewPdf` produces a “masked”/normalized PDF that avoids viewer breakage on certain files.
 * (E.g. the manual-signature window receives an already-masked PDF from `document-viewer` via `postMessage`.)
 *
 * **Sign-link exception:** `getSignatureDocument` already returns masked PDF — callers should use
 * `setViewerUrlFromContent()` and skip `getViewerDataUrl()` / `createPreviewPdf` for that path only.
 *
 * Preview is for DISPLAY ONLY: the returned URL is used solely in the PDF viewer.
 * Upload/save APIs must receive the ORIGINAL content (from fileUpload payload / document_list),
 * not the preview.
 *
 * `setViewerUrlFromContent` and `getViewerDataUrl` both use blob object URLs
 * (`live` + `previous`; never revoke the URL ngx is showing);
 * content key stays on the original base64. Submit payloads must still use original content, not the preview URL.
 *
 * Responsibilities:
 * - Normalize PDF base64 content and compute a stable key (`contentKey`) per logical document.
 * - Call `createPreviewPdf` at most once per document (per injector scope), sharing:
 *   - the last successful preview result (`lastPreviewDataUrl` — a blob: URL),
 *   - any in‑flight preview request (`previewInFlight`).
 * - Optionally toggle an external loader (`setLoading`) around the preview API.
 *
 * This service is provided at the feature‑module level (e.g. document-upload / testing-document-upload),
 * so all viewer components inside that module (`file-upload`, `signature-file-upload`, `file-upload-dtype`)
 * share the same preview cache and API de‑duplication for a given document.
 */
@Injectable()
export class PdfViewerStateService {
  private documentContentKey = '';
  private viewerUrl = '';
  private lastPreviewContentKey: string | null = null;
  private lastPreviewDataUrl: string | null = null;
  private previewInFlight: { key: string; promise: Promise<string> } | null = null;
  /** Blob currently assigned as the viewer [src]. */
  private liveUrl: string | null = null;
  /** Last live blob. Kept until the next replace so ngx is never on a revoked URL. */
  private previousUrl: string | null = null;

  constructor(private apiService: ApiService) {}

  private revokeUrl(url: string | null): void {
    if (!url) return;
    try {
      URL.revokeObjectURL(url);
    } catch {
      // Ignore revoke failures (already revoked / unsupported).
    }
    if (this.lastPreviewDataUrl === url) {
      this.lastPreviewDataUrl = null;
      this.lastPreviewContentKey = null;
    }
  }

  private revokeObjectUrl(): void {
    this.revokeUrl(this.liveUrl);
    this.revokeUrl(this.previousUrl);
    this.liveUrl = null;
    this.previousUrl = null;
  }

  /**
   * At most two blobs: the one ngx is showing, and the one it just left.
   * Never revoke `liveUrl` here — that is still (or about to be) [src].
   */
  private setObjectUrlFromBase64(base64: string): string {
    const nextUrl = URL.createObjectURL(this.base64ToPdfBlob(base64));
    if (this.previousUrl && this.previousUrl !== this.liveUrl) {
      this.revokeUrl(this.previousUrl);
    }
    this.previousUrl = this.liveUrl;
    this.liveUrl = nextUrl;
    this.viewerUrl = nextUrl;
    return nextUrl;
  }

  private base64ToPdfBlob(base64: string): Blob {
    // Match contentKey: strip whitespace so atob does not throw on padded API payloads
    // (data: URLs were more tolerant; blob construction is strict).
    const cleaned = (base64 || '').replace(/\s/g, '');
    const binary = atob(cleaned);
    const length = binary.length;
    const bytes = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: 'application/pdf' });
  }

  private getReferenceFromWindowUrl(): string | null {
    const url = window.location.href;
    if (!url.includes('?')) {
      return null;
    }
    const queryString = url.split('?')[1];
    const params = new URLSearchParams(queryString);
    return params.get('ref') ? url : null;
  }

  normalizeContent(content: string): string {
    if (!content) return '';
    if (content.startsWith('data:')) {
      const commaIdx = content.indexOf(',');
      return commaIdx >= 0 ? content.substring(commaIdx + 1) : content;
    }
    return content;
  }

  /** Same-document comparison: strip whitespace so base64 in different formats matches. */
  contentKey(base64: string): string {
    return (base64 || '').replace(/\s/g, '');
  }

  /** True when this content key is already loaded and we have a viewer URL. */
  isSameDocumentLoaded(key: string): boolean {
    return !!(key && this.documentContentKey === key && this.viewerUrl);
  }

  setDocumentKey(key: string): void {
    this.documentContentKey = key || '';
  }

  getViewerUrl(): string {
    return this.viewerUrl;
  }

  setViewerUrl(url: string): void {
    this.viewerUrl = url || '';
  }

  /**
   * Set viewer state from any string content (raw base64 or data URL).
   * Normalizes to base64, builds a blob object URL (cheaper than a giant data URL on mobile),
   * sets document key and viewer URL. Content key stays on base64 for same-doc detection.
   * @returns The object URL to pass to the viewer (same as getViewerUrl() after this call).
   */
  setViewerUrlFromContent(content: string): string {
    const base64 = this.normalizeContent(content || '');
    if (!base64) {
      this.revokeObjectUrl();
      this.documentContentKey = '';
      this.viewerUrl = '';
      return '';
    }
    const key = this.contentKey(base64);
    this.documentContentKey = key;
    return this.setObjectUrlFromBase64(base64);
  }

  /**
   * Returns a blob object URL for the masked preview PDF (same display contract as before; not a data URL).
   * - If we already have a preview for this content key, returns the cached blob URL.
   * - If a preview is already in progress for this key, returns the same in‑flight promise.
   * - Otherwise, calls `createPreviewPdf`, caches the blob URL and clears `previewInFlight` when done.
   *
   * Content key stays on the original base64. The returned URL is display-only.
   *
   * `setLoading` is optional and is called with `true`/`false` to coordinate any caller-owned loader
   * (for example `SignatureFileUploadLoadingService` used by file-upload / signature-file-upload).
   * `isCurrent` — skip blob replace when a newer load owns the viewer (do not park/revoke the live URL).
   */
  async getViewerDataUrl(
    content: string,
    setLoading?: (loading: boolean) => void,
    isCurrent?: () => boolean
  ): Promise<string> {
    const originalBase64 = this.normalizeContent(content);
    if (!originalBase64) return content;
    const key = this.contentKey(originalBase64);

    if (this.lastPreviewContentKey === key && this.lastPreviewDataUrl) {
      this.documentContentKey = key;
      this.viewerUrl = this.lastPreviewDataUrl;
      viewerLoadLog("preview.cache", { src: viewerLoadSrcKind(this.lastPreviewDataUrl) });
      return this.lastPreviewDataUrl;
    }
    if (this.previewInFlight?.key === key) {
      viewerLoadLog("preview.in-flight", { keyLen: key.length });
      return this.previewInFlight.promise;
    }

    const stillCurrent = (): boolean => !isCurrent || isCurrent();

    const promise = (async (): Promise<string> => {
      setLoading?.(true);
      viewerLoadLog("preview.start", { keyLen: key.length });
      try {        
        const reference = this.getReferenceFromWindowUrl();
        
        const previewRes = await firstValueFrom(
          this.apiService.createPreviewPdf({
            pdf_file: originalBase64,
            ...(reference ? { reference } : {}),
          })
        );
        if (!stillCurrent()) {
          const otherDocOwnsViewer = !!(this.documentContentKey && this.documentContentKey !== key);
          if (otherDocOwnsViewer) {
            viewerLoadLog("preview.stale", { after: "api", reason: "other-document" });
            return this.viewerUrl || this.lastPreviewDataUrl || '';
          }
        }
        const previewBase64 = this.normalizeContent(previewRes?.pdf_file || '');
        this.documentContentKey = key;
        const objectUrl = this.setObjectUrlFromBase64(previewBase64 || originalBase64);
        this.lastPreviewContentKey = key;
        this.lastPreviewDataUrl = objectUrl;
        viewerLoadLog("preview.done", { src: viewerLoadSrcKind(objectUrl), usedOriginal: !previewBase64 });
        return objectUrl;
      } catch {
        if (!stillCurrent()) {
          const otherDocOwnsViewer = !!(this.documentContentKey && this.documentContentKey !== key);
          if (otherDocOwnsViewer) {
            viewerLoadLog("preview.stale", { after: "error", reason: "other-document" });
            return this.viewerUrl || this.lastPreviewDataUrl || '';
          }
        }
        this.documentContentKey = key;
        const objectUrl = this.setObjectUrlFromBase64(originalBase64);
        viewerLoadLog("preview.fallback-original", { src: viewerLoadSrcKind(objectUrl) });
        return objectUrl;
      } finally {
        setLoading?.(false);
        if (this.previewInFlight?.key === key) {
          this.previewInFlight = null;
        }
      }
    })();
    this.previewInFlight = { key, promise };
    return promise;
  }

  /** Clears all viewer and preview state (e.g. reset form, delete file). */
  clear(): void {
    this.revokeObjectUrl();
    this.documentContentKey = '';
    this.viewerUrl = '';
    this.lastPreviewContentKey = null;
    this.lastPreviewDataUrl = null;
    this.previewInFlight = null;
  }
}
